import { StatementValueType } from "reducers/flow-builder.reducer";
import type {
  Attribute,
  AttributeType,
} from "pages/PeopleSettings/PeopleSettings";

export const CONTACT_PROFILE_ATTRIBUTE_KEYS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "birthday",
  "agency",
] as const;

export const DEFAULT_CONTACT_ATTRIBUTE_DEFINITIONS = [
  { name: "firstName", label: "First name", type: StatementValueType.STRING },
  { name: "lastName", label: "Last name", type: StatementValueType.STRING },
  { name: "email", label: "Email", type: StatementValueType.EMAIL },
  { name: "phone", label: "Phone", type: StatementValueType.STRING },
  { name: "birthday", label: "Birthday", type: StatementValueType.DATE },
  { name: "agency", label: "Agency", type: StatementValueType.STRING },
  { name: "lead_id", label: "Lead ID", type: StatementValueType.STRING },
  { name: "leadId", label: "Lead ID", type: StatementValueType.STRING },
  { name: "company_id", label: "Company", type: StatementValueType.STRING },
  { name: "companyId", label: "Company", type: StatementValueType.STRING },
  {
    name: "policy_status",
    label: "Policy status",
    type: StatementValueType.STRING,
  },
  {
    name: "policyStatus",
    label: "Policy status",
    type: StatementValueType.STRING,
  },
] as const;

export const DEFAULT_CONTACT_ATTRIBUTE_KEYS =
  DEFAULT_CONTACT_ATTRIBUTE_DEFINITIONS.map((attribute) => attribute.name);

export const CONTACT_MATCHING_FIELD_CANDIDATES = [
  "lead_id",
  "leadId",
  "email",
  "phone",
] as const;

const DEFAULT_CONTACT_ATTRIBUTE_KEY_SET = new Set<string>(
  DEFAULT_CONTACT_ATTRIBUTE_KEYS
);

const HIDDEN_CONTACT_ATTRIBUTE_KEY_SET = new Set([
  "createdAt",
  "isAnonymous",
  "iosDeviceToken",
  "androidToken",
  "androidDeviceToken",
  "other_ids",
  "iosDeviceTokenSetAt",
  "androidDeviceTokenSetAt",
  "null",
]);

const FALLBACK_ATTRIBUTE_TYPES: Record<StatementValueType, AttributeType> = {
  [StatementValueType.STRING]: {
    id: -1,
    name: StatementValueType.STRING,
    can_be_subtype: false,
    subtype_required: false,
    parameters_required: false,
  },
  [StatementValueType.NUMBER]: {
    id: -2,
    name: StatementValueType.NUMBER,
    can_be_subtype: true,
    subtype_required: false,
    parameters_required: false,
  },
  [StatementValueType.BOOLEAN]: {
    id: -3,
    name: StatementValueType.BOOLEAN,
    can_be_subtype: true,
    subtype_required: false,
    parameters_required: false,
  },
  [StatementValueType.EMAIL]: {
    id: -4,
    name: StatementValueType.EMAIL,
    can_be_subtype: true,
    subtype_required: false,
    parameters_required: false,
  },
  [StatementValueType.DATE]: {
    id: -5,
    name: StatementValueType.DATE,
    can_be_subtype: true,
    subtype_required: false,
    parameters_required: false,
  },
  [StatementValueType.DATE_TIME]: {
    id: -6,
    name: StatementValueType.DATE_TIME,
    can_be_subtype: true,
    subtype_required: false,
    parameters_required: false,
  },
  [StatementValueType.ARRAY]: {
    id: -7,
    name: StatementValueType.ARRAY,
    can_be_subtype: false,
    subtype_required: true,
    parameters_required: false,
  },
  [StatementValueType.OBJECT]: {
    id: -8,
    name: StatementValueType.OBJECT,
    can_be_subtype: false,
    subtype_required: false,
    parameters_required: false,
  },
};

const findDefaultDefinition = (key: string) =>
  DEFAULT_CONTACT_ATTRIBUTE_DEFINITIONS.find(
    (attribute) => attribute.name === key
  );

export const getFallbackContactAttributeType = (
  type: StatementValueType | string = StatementValueType.STRING
): AttributeType =>
  FALLBACK_ATTRIBUTE_TYPES[type as StatementValueType] ||
  FALLBACK_ATTRIBUTE_TYPES[StatementValueType.STRING];

export const getContactAttributeType = (
  type: StatementValueType | string,
  possibleAttributeTypes: AttributeType[]
): AttributeType =>
  possibleAttributeTypes.find((attributeType) => attributeType.name === type) ||
  getFallbackContactAttributeType(type);

export const getContactAttributeLabel = (key: string): string =>
  findDefaultDefinition(key)?.label || key;

export const isDefaultContactAttribute = (key: string): boolean =>
  DEFAULT_CONTACT_ATTRIBUTE_KEY_SET.has(key);

export const shouldHideContactAttribute = (key: string): boolean =>
  !key || HIDDEN_CONTACT_ATTRIBUTE_KEY_SET.has(key);

export const findRecommendedContactMatchingAttribute = <
  T extends { name: string }
>(
  attributes: T[]
): T | undefined => {
  for (const candidate of CONTACT_MATCHING_FIELD_CANDIDATES) {
    const attribute = attributes.find((item) => item.name === candidate);
    if (attribute) return attribute;
  }

  return undefined;
};

export const getContactAttributeTypeName = (
  attribute?: Pick<Attribute, "name" | "attribute_type">
): StatementValueType => {
  const defaultType = attribute?.name
    ? findDefaultDefinition(attribute.name)?.type
    : undefined;
  return (
    (attribute?.attribute_type?.name as StatementValueType | undefined) ||
    defaultType ||
    StatementValueType.STRING
  );
};

export const buildDefaultContactAttributes = (
  possibleAttributeTypes: AttributeType[] = [],
  query = ""
): Attribute[] => {
  const normalizedQuery = query.trim().toLowerCase();

  return DEFAULT_CONTACT_ATTRIBUTE_DEFINITIONS.filter((attribute) => {
    if (!normalizedQuery) return true;
    return (
      attribute.name.toLowerCase().includes(normalizedQuery) ||
      attribute.label.toLowerCase().includes(normalizedQuery)
    );
  }).map((attribute) => ({
    name: attribute.name,
    attribute_type: getContactAttributeType(
      attribute.type,
      possibleAttributeTypes
    ),
    is_primary: false,
  }));
};

export const mergeDefaultContactAttributes = (
  attributes: Attribute[],
  possibleAttributeTypes: AttributeType[] = [],
  query = ""
): Attribute[] => {
  const seenKeys = new Set(attributes.map((attribute) => attribute.name));
  const defaultAttributes = buildDefaultContactAttributes(
    possibleAttributeTypes,
    query
  ).filter((attribute) => !seenKeys.has(attribute.name));

  return [...defaultAttributes, ...attributes];
};
